import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { SearchX, Search, X, Eye } from 'lucide-react';import { useQuery } from '@tanstack/react-query';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Hero from '../components/Hero';
import Marquee from '../components/Marquee';
import ProductCard from '../components/ProductCard';
import ProductModal from '../components/ProductModal';
import StoreChooser from '../components/StoreChooser';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import Footer from '../components/Footer';
import BannerCarousel, { bannerVisivel } from '../components/BannerCarousel';
import SectionRow from '../components/SectionRow';
import CategoryCards from '../components/CategoryCards';
import { fetchCatalogo, catalogoFallback, fetchHome, HOME_PADRAO } from '../data/api';
import { LOJAS, linkWhatsApp } from '../data/produtos';

const normalizar = (texto) =>
  texto.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export default function Catalogo() {
  const [params, setParams] = useSearchParams();
  const previa = params.get('previa') === '1';
  const navigate = useNavigate();
  const [categoria, setCategoria] = useState(params.get('cat') || 'todos');
  const [busca, setBusca] = useState('');
  const [produtoAberto, setProdutoAberto] = useState(null);
  const [seletor, setSeletor] = useState({ aberto: false, produto: null });
  const catalogoRef = useRef(null);

  const { data: catalogo } = useQuery({
    queryKey: ['catalogo'],
    queryFn: fetchCatalogo,
    placeholderData: catalogoFallback,
    refetchInterval: 60_000,
  });
  const { data: home } = useQuery({
    queryKey: ['home', previa],
    queryFn: () => fetchHome(previa),
    placeholderData: HOME_PADRAO,
    refetchInterval: 60_000,
  });

  const itens = catalogo?.items || [];
  const categorias = catalogo?.categorias || [];
  const cfg = home || HOME_PADRAO;
  const secao = (id) => cfg.secoes.find((s) => s.id === id) || { ativo: false };

  const bannersAtivos = useMemo(
    () => (cfg.carrossel?.ativo ? (cfg.banners || []).filter((b) => bannerVisivel(b)) : []),
    [cfg]
  );
  const mostrarCarrossel = bannersAtivos.length > 0;

  const destaques = useMemo(() => {
    if (!cfg.destaques?.ativo || !secao('destaques').ativo) return [];
    const porId = Object.fromEntries(itens.map((p) => [p.id, p]));
    return (cfg.destaques.ids || [])
      .map((pid) => porId[pid])
      .filter(Boolean)
      .slice(0, cfg.destaques.limite || 8);
  }, [cfg, itens]);

  const ofertas = useMemo(
    () => (secao('ofertas').ativo ? itens.filter((p) => p.precoDe != null) : []),
    [cfg, itens]
  );

  const irParaCatalogo = (suave = true) =>
    catalogoRef.current?.scrollIntoView({ behavior: suave ? 'smooth' : 'auto', block: 'start' });

  const buscando = busca.trim() !== '';
  const scrollTimer = useRef(null);

  const aoDigitar = (valor) => {
    setBusca(valor);
    clearTimeout(scrollTimer.current);
    if (valor.trim() !== '') {
      // espera o usuário parar de digitar: teclas cancelam a rolagem suave no meio
      scrollTimer.current = setTimeout(() => irParaCatalogo(false), 350);
    }
  };

  useEffect(() => {
    const cat = params.get('cat');
    if (cat && categorias.some((c) => c.id === cat)) {
      setCategoria(cat);
      setTimeout(() => irParaCatalogo(), 300);
    }
  }, [params, categorias]);

  const contagem = useMemo(() => {
    const c = { todos: itens.length };
    categorias.forEach((cat) => {
      c[cat.id] = itens.filter((p) => p.categoria === cat.id).length;
    });
    return c;
  }, [itens, categorias]);

  const filtrados = useMemo(() => {
    const termos = normalizar(busca.trim()).split(/\s+/).filter(Boolean);
    return itens.filter((p) => {
      const okCategoria = categoria === 'todos' || p.categoria === categoria;
      if (!okCategoria) return false;
      if (!termos.length) return true;
      const alvo = normalizar(
        [p.nome, p.marca, p.categoriaNome, p.categoria, p.sku, p.descricao, ...(p.detalhes || [])]
          .filter(Boolean)
          .join(' ')
      );
      return termos.every((t) => alvo.includes(t));
    });
  }, [categoria, busca, itens]);

  const abrirSeletor = (produto = null) => setSeletor({ aberto: true, produto });
  const fecharSeletor = () => setSeletor({ aberto: false, produto: null });

  const destinoBanner = (b) => {
    const v = b.destinoValor || '';
    switch (b.destinoTipo) {
      case 'produto': navigate(`/produto/${v}`); break;
      case 'categoria': setParams({ cat: v }); break;
      case 'destaques': document.getElementById('destaques')?.scrollIntoView({ behavior: 'smooth' }); break;
      case 'ofertas': document.getElementById('ofertas')?.scrollIntoView({ behavior: 'smooth' }); break;
      case 'pagina': navigate(v); break;
      case 'whatsapp': window.open(linkWhatsApp(LOJAS[0], null), '_blank', 'noopener,noreferrer'); break;
      case 'externo': window.open(v, '_blank', 'noopener,noreferrer'); break;
      default: break;
    }
  };

  const secaoProdutos = (
    <div id="catalogo" ref={catalogoRef} className="scroll-mt-20 py-10 sm:py-12" key="produtos">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="flex flex-col gap-2"
      >
        <p className="text-[11px] font-extrabold uppercase tracking-[0.25em] text-mk-red">{buscando ? 'Busca' : 'Vitrine virtual'}</p>
        <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
          {buscando ? `Resultados para "${busca.trim()}"` : secao('produtos').titulo || 'Nossos produtos'}
        </h2>
        {secao('produtos').subtitulo && <p className="text-sm sm:text-base text-slate-500">{secao('produtos').subtitulo}</p>}
      </motion.div>

      {categoria !== 'todos' && (
        <div className="mt-6 flex items-center gap-2">
          <span data-testid="active-category-chip" className="inline-flex items-center gap-2 rounded-full bg-mk-blue px-4 py-2 text-sm font-bold text-white">
            {categorias.find((c) => c.id === categoria)?.nome || categoria}
            <button
              data-testid="active-category-clear"
              onClick={() => setCategoria('todos')}
              aria-label="Mostrar todos os produtos"
              className="rounded-full bg-white/20 p-0.5 hover:bg-white/35"
            >
              <X size={14} />
            </button>
          </span>
        </div>
      )}

      {filtrados.length > 0 ? (
        <div data-testid="product-grid" className="mt-6 grid grid-cols-1 gap-4 min-[420px]:grid-cols-2 min-[420px]:gap-3 sm:gap-5 lg:grid-cols-3 xl:grid-cols-4">
          {filtrados.map((produto, i) => (
            <ProductCard key={produto.id} produto={produto} index={i} onDetalhes={setProdutoAberto} onWhatsApp={abrirSeletor} />
          ))}
        </div>
      ) : (
        <div className="mt-6 flex flex-col items-center gap-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50 py-16 text-center">
          <SearchX size={40} className="text-slate-300" />
          <p className="text-sm sm:text-base font-medium text-slate-500">Nenhum produto encontrado para "{busca}".</p>
          <button
            data-testid="search-clear-button"
            onClick={() => { setBusca(''); setCategoria('todos'); }}
            className="rounded-full bg-mk-blue px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-mk-bluelight"
          >
            Limpar busca
          </button>
        </div>
      )}
    </div>
  );

  const blocos = cfg.secoes
    .filter((s) => s.ativo)
    .map((s) => {
      // durante a busca, esconder vitrines para o resultado ficar logo abaixo do campo
      if (buscando && (s.id === 'destaques' || s.id === 'ofertas')) return null;
      if (s.id === 'destaques')
        return <SectionRow key="destaques" id="destaques" titulo={s.titulo} subtitulo={s.subtitulo} produtos={destaques} onDetalhes={setProdutoAberto} onWhatsApp={abrirSeletor} cor="blue" />;
      if (s.id === 'ofertas')
        return <SectionRow key="ofertas" id="ofertas" titulo={s.titulo} subtitulo={s.subtitulo} produtos={ofertas} onDetalhes={setProdutoAberto} onWhatsApp={abrirSeletor} cor="red" />;
      if (s.id === 'produtos') return secaoProdutos;
      return null;
    })
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-white font-body text-slate-900 antialiased">
      {previa && (
        <div data-testid="previa-banner" className="sticky top-0 z-50 flex items-center justify-center gap-2 bg-amber-400 px-4 py-2 text-center text-xs font-bold text-amber-900">
          <Eye size={14} /> Modo prévia — este é o rascunho da página inicial. As alterações ainda não estão públicas.
        </div>
      )}
      <Header onWhatsAppGeral={() => abrirSeletor(null)} />

      {mostrarCarrossel ? (
        <>
          <BannerCarousel banners={bannersAtivos} intervaloSeg={cfg.carrossel.intervaloSeg} onDestino={destinoBanner} />
          <div className="border-b border-slate-100 bg-white py-4">
            <div className="relative mx-auto max-w-2xl px-4">
              <Search size={18} className="absolute left-8 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="hero-search-input"
                type="text"
                value={busca}
                onChange={(e) => aoDigitar(e.target.value)}
                placeholder="Busque por produto, marca ou modelo..."
                className="w-full rounded-full border border-slate-200 bg-slate-50 py-3.5 pl-11 pr-11 text-sm sm:text-base text-slate-900 placeholder-slate-400 shadow-sm outline-none focus:ring-2 focus:ring-mk-blue/30"
              />
              {busca && (
                <button data-testid="hero-search-clear-button" onClick={() => setBusca('')} aria-label="Limpar busca" className="absolute right-8 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-slate-400 hover:bg-slate-200">
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
        </>
      ) : (
        <Hero busca={busca} setBusca={aoDigitar} onVerProdutos={irParaCatalogo} total={itens.length} totalCategorias={categorias.length} />
      )}
      <Marquee />
      {secao('categorias').ativo && !buscando && (
        <CategoryCards categorias={categorias} onEscolher={(id) => { setCategoria(id); setBusca(''); setTimeout(irParaCatalogo, 150); }} />
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14">{blocos}</main>

      <Footer />
      <FloatingWhatsApp onClick={() => abrirSeletor(null)} />

      <AnimatePresence>
        {produtoAberto && (
          <ProductModal produto={produtoAberto} onFechar={() => setProdutoAberto(null)} onWhatsApp={(p) => abrirSeletor(p)} />
        )}
        {seletor.aberto && <StoreChooser produto={seletor.produto} onFechar={fecharSeletor} />}
      </AnimatePresence>
    </div>
  );
}
